import { EntryType } from '@morph-mapper/types';
import { definePlugin } from '../../framework';
import { resolveCheckingPrompt } from './prompt-registry';

type CheckingKeywords = {
  companyName: string | null;
  templateHeaders: string[];
};

const MAX_TEMPLATE_HEADERS = 2;

type CheckingState = {
  domain?: string;
};

let cachedCheckingKeywords: CheckingKeywords | null = null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    : [];

export function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const CONTAINS_DIGIT_RE = /\d/;

export function isStableKeyword(keyword: string): boolean {
  return keyword.trim().length > 0 && !CONTAINS_DIGIT_RE.test(keyword);
}

export function appearsInText(keyword: string, textContent: string): boolean {
  return textContent.toLowerCase().includes(keyword.trim().toLowerCase());
}

export function filterCheckingKeywords(
  keywords: CheckingKeywords,
  textContent: string,
): CheckingKeywords {
  const isUsable = (keyword: string) =>
    isStableKeyword(keyword) && appearsInText(keyword, textContent);

  return {
    companyName:
      keywords.companyName && isUsable(keywords.companyName) ? keywords.companyName : null,
    templateHeaders: keywords.templateHeaders.filter(isUsable).slice(0, MAX_TEMPLATE_HEADERS),
  };
}

export function parseCheckingResponse(modelText: string): CheckingKeywords | null {
  try {
    const fenceMatch = modelText.match(/```(?:json)?\s*([\s\S]*?)```/i);
    const cleaned = (fenceMatch ? fenceMatch[1] : modelText).trim();
    const parsed = JSON.parse(cleaned);

    if (!isRecord(parsed)) return null;

    return {
      companyName: typeof parsed['companyName'] === 'string' ? parsed['companyName'].trim() || null : null,
      templateHeaders: asStringArray(parsed['templateHeaders']),
    };
  } catch {
    return null;
  }
}

function regexTest(pattern: string, variable: string, flags = 'gi') {
  return {
    $RegExp: pattern,
    $flag: flags,
    $result: { '$data.test': variable },
  };
}

const matchesKeyword = (keyword: string) =>
  regexTest(escapeRegExp(keyword.trim()), '$pdfString');

const anyOf = (conditions: unknown[]) =>
  conditions.length === 1 ? conditions[0] : { '$utils.or': conditions };

export function buildCheckingSnippet(keywords: CheckingKeywords) {
  const headers = keywords.templateHeaders.map(matchesKeyword);
  const conditions: unknown[] = [];

  if (keywords.companyName) {
    conditions.push(matchesKeyword(keywords.companyName));
    if (headers.length > 0) conditions.push(anyOf(headers));
  } else {
    conditions.push(...headers);
  }

  if (conditions.length === 0) return true;
  if (conditions.length === 1) return conditions[0];
  return { '$utils.and': conditions };
}

const MAX_CHARS = 20_000;

function buildCheckingPrompt(): string {
  return (
    'Identify the minimal set of characterizing signals that distinguish this document\'s ' +
    'template from other templates, for use in a template-matching "checking" condition. ' +
    'These signals will be required together (ANDed), so fewer, stronger signals are ' +
    'better than many weak ones — prefer precision over coverage.\n' +
    'A signal is only valid if it would stay exactly the same on every other document that ' +
    'uses this same template.\n' +
    '"companyName" is the single name of the company that issued/created this document ' +
    '(its own letterhead/branding) — not any other company mentioned in the document.\n' +
    '"templateHeaders" is at most ONE OR TWO of the most distinctive fixed form/field labels ' +
    'or section headers on this template — pick only what most uniquely identifies this ' +
    'exact template, not every label you can find.\n' +
    'NEVER pick a signal that is transactional data specific to this one document, even if ' +
    'it looks like a proper noun or contains no digits — this includes vessel names, ' +
    'voyage/booking/carrier identifiers, the names of any company OTHER than the one that ' +
    'issued this document (customer, trucking company, terminal, depot), remarks or ' +
    'comments written for this specific shipment, dates, and place names tied to this ' +
    'specific shipment\'s route.\n' +
    'Return ONLY a JSON object with this exact shape:\n' +
    '{\n' +
    '  "companyName": string | null,\n' +
    '  "templateHeaders": string[]\n' +
    '}' +
    '\n\nDOCUMENT TEXT FOLLOWS:\n'
  );
}

export const Checking = definePlugin<CheckingState>({
  name: 'Checking',
  version: '0.3.2',
  initialState: {},
  settings: {
    autofill: {
      name: 'Auto-fill suggestions',
      type: 'boolean',
      default: true,
    },
  },

  bootstrap: async ({ textContent, state, progress, extractWithLLM }) => {
    cachedCheckingKeywords = null;

    if (!textContent || !textContent.trim()) return;

    progress(10, 'Finding company name and headers');

    const clipped =
      textContent.length > MAX_CHARS ? textContent.slice(0, MAX_CHARS) : textContent;
    const prompt = buildCheckingPrompt() + clipped;

    try {
      const promptName = resolveCheckingPrompt(state.domain);
      const response = await extractWithLLM(prompt, promptName);

      if (!response.isOk()) {
        return;
      }

      const modelText = await response.value.text;
      if (!modelText) return;

      const parsed = parseCheckingResponse(modelText);
      cachedCheckingKeywords = parsed
        ? filterCheckingKeywords(parsed, textContent)
        : null;
      progress(30, 'Company name and headers found');
    } catch {
      return;
    } finally {
      // 85 is the convention for "this plugin is finished" — the pipeline
      // drives the remaining 15%. In finally so the failure paths above settle
      // the bar too, instead of leaving it frozen at 10%. Everything after
      // bootstrap happens per field, which cannot report progress.
      progress(85, 'Checking ready');
    }
  },

  fieldSuggestions({ entry, settings }) {
    if (entry.key !== 'checking') return [];
    if (!cachedCheckingKeywords) return [];

    return [
      {
        value: 'AI-suggested checking condition',
        snippet: buildCheckingSnippet(cachedCheckingKeywords) as any,
        type: EntryType.Code,
        autofill: Boolean(settings['autofill']),
      },
    ];
  },
});
